import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { leadsApi, feasibilityApi, ApiError } from "@/api";
import type { Lead, LeadDetail, ProjectBrief, ProjectBriefExtension, ProjectBriefProperty } from "@/types";
import { Button, Spinner, TextField, TextAreaField, SelectField } from "@/components/ui";
import { leadName } from "@/lib/leads";
import { EAVES_BANDS, HEIGHT_BANDS, mergeBrief, missingRequired, normalizeBrief, prettyLabel } from "@/lib/feasibility";
import { BriefFields } from "./BriefFields";

/**
 * Feasibility report flow (spec §6). This screen is a pre-filled REVIEW + top-up
 * + generate — NOT a from-scratch form. The site address and project details are
 * captured in the lead drawer (§5.3) and stored on `project_brief`; here we
 * prefill from that, run the debounced Modal `/analyze` loop for follow-ups +
 * readiness, let the user top up anything missing, then on generate:
 *   1. PATCH the (merged) brief back onto the lead,
 *   2. POST /generate → call_id, poll /result ~2s until done,
 *   3. data-URI PDF → Blob → multipart POST /feasibility-report,
 *   4. hand the updated lead back so the drawer shows the result.
 *
 * There is NO postcode lookup here — that lives on the drawer. `quoteUrl` is the
 * linked quote's public URL, or "" when there's no published quote; empty is fine,
 * and the rendered report falls back to generic next-step copy with no quote CTA.
 */

type Phase = "form" | "generating" | "polling" | "storing";

const ANALYZE_DEBOUNCE_MS = 600;
const POLL_INTERVAL_MS = 2000;
/** Ceiling on the poll loop. The engine's own function timeout is 300s (see
 *  feasibility/app.py), so this sits just above it — a run that dies inside Modal
 *  reports its own failure first, and this only fires when the polling itself gets
 *  nowhere. Without a ceiling a stuck run spins until the tab is reloaded. */
const POLL_TIMEOUT_MS = 6 * 60 * 1000;

/** "45s" / "2m 05s" — the run takes minutes, so the spinner shows the clock. */
function elapsedLabel(ms: number): string {
  const total = Math.floor(ms / 1000);
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
}

function num(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Drop "", null, undefined keys before sending to Modal. */
function compact<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== "" && v !== null && v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

interface SiteFields {
  address: string;
  council: string;
  uprn: string;
  latitude?: number;
  longitude?: number;
}

/** Single-line site address rebuilt from the lead's own columns, in the same shape
 *  a lookup pick stores on the brief (line 1–3 + post town). */
function leadAddress(lead: Lead): string {
  return [lead.site_address_line_1, lead.site_address_line_2, lead.site_address_line_3, lead.site_city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");
}

export function FeasibilityModal({
  lead,
  quoteUrl,
  onStored,
  onClose,
}: {
  lead: LeadDetail;
  quoteUrl: string;
  onStored: (lead: Lead) => void;
  onClose: () => void;
}) {
  // Migrate any pre-split stored brief to the property + extensions[] shape on the way in.
  const brief = normalizeBrief(lead.project_brief);
  const [description, setDescription] = useState(lead.project_description ?? "");
  // The brief wins, but it only exists once the lead drawer's own lookup has run.
  // An address picked in the Add-lead drawer lands on the lead's columns with no
  // brief at all, so every site field falls back to its column — otherwise the LPA,
  // UPRN and lat/lng captured at creation arrive here blank. A stored 0 lat/lng is
  // the Add-lead "not looked up" sentinel, so it reads as absent.
  const [site, setSite] = useState<SiteFields>({
    address: brief.address || leadAddress(lead),
    council: brief.council || lead.site_district || "",
    uprn: brief.uprn || lead.uprn || "",
    latitude: brief.latitude ?? (lead.lat || undefined),
    longitude: brief.longitude ?? (lead.lng || undefined),
  });
  const [budgetBand, setBudgetBand] = useState(brief.budget_band ?? "");
  const [property, setProperty] = useState<ProjectBriefProperty>({ ...(brief.property ?? {}) });
  const [extensions, setExtensions] = useState<ProjectBriefExtension[]>(brief.extensions ?? [{}]);

  // analyze state
  const [analyzing, setAnalyzing] = useState(false);
  const [ready, setReady] = useState(false);
  const [questions, setQuestions] = useState<feasibilityApi.Question[]>([]);
  const [constraints, setConstraints] = useState<Record<string, unknown> | null>(null);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const analyzeSeq = useRef(0);

  // generate state
  const [phase, setPhase] = useState<Phase>("form");
  const [error, setError] = useState<string | undefined>();
  const alive = useRef(true);
  // Set `alive` on every mount, not only the first. StrictMode runs effects
  // mount → cleanup → mount in dev, so a cleanup-only effect leaves the ref stuck
  // at false for the component's whole life — which aborted the poll loop before
  // its first request and left the spinner running with no error and no request.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  // Bumped by the cancel affordances (and by each new run) so an in-flight
  // generate() unwinds quietly instead of writing state a later run owns.
  const genSeq = useRef(0);

  const busy = phase !== "form";

  // Elapsed clock while generating — a multi-minute wait behind a static spinner
  // is indistinguishable from a hang.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!busy) {
      setElapsedMs(0);
      return;
    }
    const startedAt = Date.now();
    const handle = setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => clearInterval(handle);
  }, [busy]);

  function setSiteField(patch: Partial<SiteFields>) {
    setSite((s) => ({ ...s, ...patch }));
  }
  function setProp(patch: Partial<ProjectBriefProperty>) {
    setProperty((p) => ({ ...p, ...patch }));
  }
  // Follow-up routing (multi-type): budget_band is top-level; a property-level question
  // (extension_index null) writes to the property block; a per-line question writes to that line.
  function answer(q: feasibilityApi.Question, v: unknown) {
    if (q.key === "budget_band") {
      setBudgetBand(String(v ?? ""));
    } else if (q.extension_index == null) {
      setProp({ [q.key]: v } as Partial<ProjectBriefProperty>);
    } else {
      const i = q.extension_index;
      setExtensions((arr) => arr.map((e, idx) => (idx === i ? { ...e, [q.key]: v } : e)));
    }
  }
  function answerValue(q: feasibilityApi.Question): unknown {
    if (q.key === "budget_band") return budgetBand;
    if (q.extension_index == null) return (property as Record<string, unknown>)[q.key];
    return (extensions[q.extension_index] as Record<string, unknown> | undefined)?.[q.key];
  }

  // The brief we'd persist / send (site + budget + property block + extensions[]).
  const currentBrief: ProjectBrief = useMemo(
    () => ({
      address: site.address,
      council: site.council,
      uprn: site.uprn,
      latitude: site.latitude,
      longitude: site.longitude,
      budget_band: budgetBand,
      property,
      extensions,
    }),
    [site, budgetBand, property, extensions],
  );

  function buildAnalyzeBody(): feasibilityApi.AnalyzeBody {
    return {
      ...compact({
        description,
        address: site.address,
        council: site.council,
        uprn: site.uprn,
        latitude: site.latitude,
        longitude: site.longitude,
        budget_band: budgetBand,
      }),
      property: compact(property as Record<string, unknown>),
      extensions: extensions.map((e) => compact(e as Record<string, unknown>)),
    };
  }

  // Debounced /analyze loop with a stale-response guard.
  const analyzeSnapshot = JSON.stringify(buildAnalyzeBody());
  useEffect(() => {
    if (busy) return;
    const body = JSON.parse(analyzeSnapshot) as feasibilityApi.AnalyzeBody;
    // `description` is the engine's only required analyze field, and nothing else
    // substitutes for it: posting a site-only body 422s. A lead whose site came from
    // the Add-lead lookup arrives here with every site field filled and no description,
    // so without this gate the modal opens straight onto a validation dump.
    if (!body.description?.trim()) {
      setReady(false);
      setQuestions([]);
      setConstraints(null);
      setAnalyzeError(null);
      return;
    }
    const seq = ++analyzeSeq.current;
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      setAnalyzing(true);
      try {
        const data = await feasibilityApi.analyzeFeasibility(body, controller.signal);
        if (seq !== analyzeSeq.current) return;
        setAnalyzing(false);
        setAnalyzeError(null);
        setReady(Boolean(data.ready));
        setQuestions(Array.isArray(data.questions) ? data.questions : []);
        setConstraints(data.constraints ?? null);
      } catch (e) {
        if (seq !== analyzeSeq.current) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        setAnalyzing(false);
        setReady(false);
        setAnalyzeError(e instanceof Error ? e.message : "Analyze failed.");
      }
    }, ANALYZE_DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [analyzeSnapshot, busy]);

  async function pollUntilDone(
    callId: string,
    abandoned: () => boolean,
  ): Promise<feasibilityApi.FeasibilityResult> {
    const startedAt = Date.now();
    for (;;) {
      if (abandoned()) throw new Error("cancelled");
      const r = await feasibilityApi.pollFeasibilityResult(callId);
      if (r.status === "done" && r.result) return r.result;
      if (r.status === "error") throw new Error(r.error || "Report generation failed.");
      if (r.status === "expired") throw new Error("This result expired (results live ~7 days) — re-run to generate a fresh report.");
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        throw new Error("The engine hasn't returned a report after 6 minutes. It may still finish — try again shortly.");
      }
      await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
    }
  }

  async function generate() {
    // Own this run: anything started earlier now reads as abandoned.
    const seq = ++genSeq.current;
    const abandoned = () => !alive.current || genSeq.current !== seq;

    setError(undefined);
    setPhase("generating");
    try {
      // 1. Persist the (merged) brief — top-ups captured here shouldn't be lost.
      await leadsApi.patchLead(lead.id, { project_brief: mergeBrief(lead.project_brief, currentBrief) });
      if (abandoned()) return;

      // 2. Mint the report token BEFORE generating — the engine publishes under it, and this
      //    also writes the web url onto the lead so the drawer link points at the report page.
      //    The token is stable across regeneration; only the version (and so the R2 keys) moves.
      const { token, version } = await leadsApi.beginFeasibilityReport(lead.id);
      if (abandoned()) return;

      // 3. Kick off generation (property block + extensions[]; site/budget top-level).
      const { call_id } = await feasibilityApi.generateFeasibility({
        ...buildAnalyzeBody(),
        client_name: leadName(lead),
        quote_url: quoteUrl,
        report_token: token,
        report_version: version,
        // The variant the report is rendered with, and the one every session is stamped with.
        report_cta_variant: quoteUrl ? "quote" : "general",
      });
      if (abandoned()) return;

      // 4. Poll.
      setPhase("polling");
      const result = await pollUntilDone(call_id, abandoned);
      if (abandoned()) return;

      // 5. Record the summary. No PDF upload any more — the engine put both artefacts in R2 and
      //    the Worker serves them behind the token, so the data-URI → Blob → multipart step is
      //    gone along with holding a 400KB+ data URI in memory.
      setPhase("storing");
      const { pdf_url: _pdf_url, ...summary } = result;
      const updated = await leadsApi.storeFeasibilityReport(lead.id, summary);
      if (abandoned()) return;
      onStored(updated);
      onClose();
    } catch (err) {
      if (abandoned()) return;
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not generate the report.");
      setPhase("form");
    }
  }

  /** Stop waiting on the current run. The engine keeps going — its report just
   *  won't be collected, and the lead keeps whichever report it already had. */
  function cancelGeneration() {
    genSeq.current += 1;
    setPhase("form");
    setError("Stopped waiting. The engine may still finish this run in the background — re-generate when you're ready.");
  }

  /** Closing mid-run abandons the wait rather than leaving it orphaned. */
  function handleClose() {
    genSeq.current += 1;
    onClose();
  }

  // Gate on /analyze readiness; allow a fallback when analyze can't be reached
  // (e.g. CORS) but every required field is present, so a flaky engine doesn't
  // hard-block a complete brief.
  const requiredComplete = missingRequired(currentBrief).length === 0 && !!description.trim();
  const canGenerate = !busy && (ready || (!!analyzeError && requiredComplete));

  // With no description the analyze loop is held (the engine requires one), so say so
  // rather than leaving a prefilled form sitting under "fill in the details".
  let statusText = description.trim()
    ? "Fill in the details to begin."
    : "Add a project description to check feasibility.";
  if (analyzing) statusText = "Checking feasibility…";
  else if (analyzeError) statusText = requiredComplete ? "Couldn't reach the analyzer — you can still generate." : analyzeError;
  else if (ready) statusText = "Ready to generate the report.";
  else if (questions.length > 0) statusText = "Answer the follow-ups below to continue.";

  return (
    // Backdrop clicks stay inert mid-run: dismissing a multi-minute job by
    // mis-click is worse than asking for the explicit button. The ✕ and the
    // footer button are always live, so the dialog is never a trap.
    <div className="picker__overlay" onClick={busy ? undefined : handleClose}>
      <div className="feaso" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Feasibility report">
        <header className="picker__head">
          <h3>Feasibility report</h3>
          <button type="button" className="lead-drawer__close" onClick={handleClose} aria-label="Close">✕</button>
        </header>

        {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

        {busy ? (
          <div className="feaso__progress">
            <Spinner />
            <p>
              {phase === "generating" && "Starting the report engine…"}
              {phase === "polling" && `Building the report — usually 2–3 minutes… (${elapsedLabel(elapsedMs)})`}
              {phase === "storing" && "Saving the report…"}
            </p>
          </div>
        ) : (
          <div className="feaso__body">
            {/* Sticky (see leads.css): stays in view while a long brief scrolls. aria-live so the
                checking → ready change is announced, not just recoloured. */}
            <p
              className={`feaso__status feaso__status--${ready ? "ready" : analyzeError ? "wait" : "idle"}`}
              role="status"
              aria-live="polite"
            >
              {statusText}
            </p>

            <TextAreaField
              label="Project description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="single storey rear extension with bi-fold doors"
            />

            <span className="feaso__legend">Site (from the lead — top up if needed)</span>
            <TextField label="Address" value={site.address} onChange={(e) => setSiteField({ address: e.target.value })} />
            <div className="lead-drawer__two">
              <TextField label="Council (LPA)" value={site.council} onChange={(e) => setSiteField({ council: e.target.value })} />
              <TextField label="UPRN" value={site.uprn} onChange={(e) => setSiteField({ uprn: e.target.value })} />
            </div>
            <div className="lead-drawer__two">
              <TextField label="Latitude" type="number" value={site.latitude ?? ""} onChange={(e) => setSiteField({ latitude: num(e.target.value) })} />
              <TextField label="Longitude" type="number" value={site.longitude ?? ""} onChange={(e) => setSiteField({ longitude: num(e.target.value) })} />
            </div>

            <span className="feaso__legend">Property &amp; extensions</span>
            <BriefFields
              budgetBand={budgetBand}
              property={property}
              extensions={extensions}
              onBudget={setBudgetBand}
              onProperty={setProp}
              onExtensions={setExtensions}
            />

            {questions.length > 0 ? (
              <div className="feaso__followups">
                <strong>Smart follow-ups{analyzing ? "…" : ""}</strong>
                <p className="feaso__followups-sub">A few answers will sharpen the assessment.</p>
                {questions.map((q) => (
                  <QuestionField
                    key={`${q.extension_index ?? "p"}:${q.key}`}
                    q={q}
                    value={answerValue(q)}
                    onChange={(v) => answer(q, v)}
                    multi={extensions.length > 1}
                  />
                ))}
              </div>
            ) : null}

            {constraints && Object.keys(constraints).length > 0 ? (
              <div className="feaso__constraints">
                {Object.entries(constraints).map(([k, v]) => {
                  const on = v === true || v === "true";
                  return (
                    <span key={k} className={`feaso__pill${on ? " feaso__pill--on" : ""}`}>
                      {prettyLabel(k)}{on ? "" : ": no"}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
        )}

        <footer className="picker__foot">
          <Button variant="ghost" onClick={busy ? cancelGeneration : handleClose}>
            {busy ? "Stop waiting" : "Cancel"}
          </Button>
          <Button variant="primary" onClick={generate} loading={busy} disabled={!canGenerate}>
            Generate report
          </Button>
        </footer>
      </div>
    </div>
  );
}

/** Renders one dynamic /analyze follow-up by its declared type. When the report has more than
 *  one extension, a per-line question is labelled with the element it targets. */
function QuestionField({ q, value, onChange, multi }: { q: feasibilityApi.Question; value: unknown; onChange: (v: unknown) => void; multi?: boolean }): ReactNode {
  const target =
    multi && q.extension_index != null ? (
      <span className="feaso__q-target">Extension {q.extension_index + 1}</span>
    ) : null;
  // Height questions answer in GPDO bands, not free numbers — same bands as BriefFields.
  const bands = q.key === "height_m" ? HEIGHT_BANDS : q.key === "eaves_height_m" ? EAVES_BANDS : null;
  let control: ReactNode;
  if (bands) {
    control = (
      <SelectField
        label={q.prompt}
        hint={q.rationale}
        value={typeof value === "number" ? String(value) : ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
      >
        <option value="">—</option>
        {bands.map((b) => (
          <option key={b.value} value={String(b.value)}>{b.label}</option>
        ))}
      </SelectField>
    );
  } else if (q.type === "choice") {
    control = (
      <SelectField label={q.prompt} hint={q.rationale} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {(q.options ?? []).map((o) => (
          <option key={o} value={o}>{prettyLabel(o)}</option>
        ))}
      </SelectField>
    );
  } else if (q.type === "number") {
    control = (
      <TextField
        label={`${q.prompt}${q.unit ? ` (${q.unit})` : ""}`}
        hint={q.rationale}
        type="number"
        inputMode="decimal"
        value={typeof value === "number" ? value : ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
      />
    );
  } else if (q.type === "boolean") {
    control = (
      <div className="feaso__bool">
        <span className="field__label">{q.prompt}</span>
        <div className="feaso__seg">
          <button type="button" className={value === true ? "is-on" : ""} onClick={() => onChange(true)}>Yes</button>
          <button type="button" className={value === false ? "is-on" : ""} onClick={() => onChange(false)}>No</button>
        </div>
        {q.rationale ? <p className="field__desc">{q.rationale}</p> : null}
      </div>
    );
  } else {
    control = (
      <TextField label={q.prompt} hint={q.rationale} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />
    );
  }
  return <div className="feaso__q">{target}{control}</div>;
}
